import { MessageSquare, ClipboardList, ThumbsUp, Phone } from 'lucide-react';

const CAVEMAN_WEAPONS = "/images/caveman-weapons.png";

const steps = [
  {
    icon: MessageSquare,
    title: 'Consultation',
    description: 'Let us help you figure out what you want to do. We will go over how a pawn is different than conventional lending and why, short term, it may be the fastest way to get you out of a bind. Or we can just buy your item outright. Either way, we will work with you. The best part? There are no credit checks!',
    tip: 'Building a good relationship with your local pawn broker can be a really great thing.',
  },
  {
    icon: ClipboardList,
    title: 'Planning',
    description: 'Figure out how much you need to borrow, then bring one or more items into Cave City Pawn. We will appraise your items using our extensive reference library. If they appraise out, we can lend you what you need. Think about a realistic timeline to pay back the loan - this will always save you money.',
    tip: 'Bring valid ID and know what you need. Multiple items can increase your loan amount.',
  },
  {
    icon: ThumbsUp,
    title: 'Results',
    description: 'Pawn shops do charge higher interest rates because of the financial risk we take, but it is less expensive than letting your water or power get shut off and having to pay all those reconnection fees. Sometimes you just need a little money to get you through a pinch. When the grass is a little greener, you can close your ticket and pick up your items!',
    tip: 'Pay back early to save on interest. Your items are safely stored until you reclaim them.',
  },
];

export default function PawnProcessSection() {
  return (
    <section
      id="pawn-process"
      data-testid="pawn-process-section"
      className="relative py-20 md:py-28 bg-dirt-brown noise-overlay overflow-hidden torch-glow"
      aria-label="How to sell or pawn your items at Cave City Pawn"
    >
      {/* Decorative boulders top */}
      <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute -top-1 left-0 w-full">
        <path d="M0 0L60 12C120 24 240 48 360 54C480 60 600 48 720 42C840 36 960 36 1080 42C1200 48 1320 60 1380 66L1440 72V0H0Z" fill="#F4EBD9"/>
      </svg>

      {/* Caveman sticker */}
      <img
        src={CAVEMAN_WEAPONS}
        alt="Cartoon caveman and cavewoman mascots of Cave City Pawn Shop"
        className="absolute right-4 md:right-12 top-20 md:top-28 w-28 md:w-40 lg:w-52 caveman-float opacity-80 z-10 hidden md:block"
        loading="lazy"
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-14 md:mb-18 max-w-3xl mx-auto">
          <p className="text-sm font-body font-semibold text-accent uppercase tracking-widest mb-3">How It Works</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading text-bone-white uppercase mb-5 chisel-heading">
            Sell or <span className="text-accent">Pawn</span> Your Items
          </h2>
          <p className="text-base md:text-lg text-stone-light font-body leading-relaxed mt-6">
            We will lend on most anything of value. Life sometimes gets tough and we have all been there. 
            Come in today and let one of our team members help you. <strong className="text-accent">Helping you get back on your feet again!</strong>
          </p>
        </div>

        {/* Pawn vs Sell comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <div
            data-testid="pawn-option-card"
            className="reveal-child stone-card bg-bone-white/8 border-4 border-accent/20 rounded-rock-lg p-6 md:p-8 backdrop-blur-sm hover:border-accent/40"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
                <i className="fas fa-handshake text-accent text-lg"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-heading text-accent uppercase">Pawn It</h3>
            </div>
            <p className="text-base text-stone-light font-body leading-relaxed mb-4">
              Need quick cash but want to keep your stuff? A pawn loan lets you borrow money against your item. 
              Pay back the loan plus interest, and you get your item back. No credit checks, no hassle.
            </p>
            <ul className="space-y-2 text-sm text-stone-light font-body">
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5"><i className="fas fa-check-circle"></i></span>
                No credit check required
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5"><i className="fas fa-check-circle"></i></span>
                Get your items back when you repay
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5"><i className="fas fa-check-circle"></i></span>
                Quick same-day cash in hand
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent mt-0.5"><i className="fas fa-check-circle"></i></span>
                Items safely stored and insured
              </li>
            </ul>
          </div>
          <div
            data-testid="sell-option-card"
            className="reveal-child stone-card bg-bone-white/8 border-4 border-magma-red/20 rounded-rock-lg p-6 md:p-8 backdrop-blur-sm hover:border-magma-red/40"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-magma-red/20 rounded-full flex items-center justify-center">
                <i className="fas fa-dollar-sign text-magma-red text-lg"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-heading text-magma-red uppercase">Sell It</h3>
            </div>
            <p className="text-base text-stone-light font-body leading-relaxed mb-4">
              Ready to part ways? We buy items outright for fair prices. Bring in your firearms, electronics, 
              instruments, jewelry, or tools and walk out with cash. Fast, fair, and no strings attached.
            </p>
            <ul className="space-y-2 text-sm text-stone-light font-body">
              <li className="flex items-start gap-2">
                <span className="text-magma-red mt-0.5"><i className="fas fa-check-circle"></i></span>
                Immediate cash payment
              </li>
              <li className="flex items-start gap-2">
                <span className="text-magma-red mt-0.5"><i className="fas fa-check-circle"></i></span>
                Fair market value appraisals
              </li>
              <li className="flex items-start gap-2">
                <span className="text-magma-red mt-0.5"><i className="fas fa-check-circle"></i></span>
                No obligations or follow-ups
              </li>
              <li className="flex items-start gap-2">
                <span className="text-magma-red mt-0.5"><i className="fas fa-check-circle"></i></span>
                Expert valuation with 35+ years experience
              </li>
            </ul>
          </div>
        </div>

        {/* Process Steps */}
        <div className="text-center mb-10">
          <p className="text-sm font-body font-semibold text-accent/70 uppercase tracking-widest mb-2">3 Simple Steps</p>
          <h3 className="text-2xl md:text-3xl font-heading text-bone-white uppercase">
            The Process
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={step.title}
                data-testid={`process-step-${i + 1}`}
                className={`reveal-child stone-card bg-bone-white/8 border-4 border-bone-white/15 rounded-rock-lg p-6 md:p-8 text-center backdrop-blur-sm hover:border-accent/30 ${i < 2 ? 'step-connector' : ''}`}
              >
                <div className="w-16 h-16 bg-magma-red rounded-full flex items-center justify-center mx-auto mb-4 shadow-deep">
                  <span className="text-bone-white font-heading text-2xl">{i + 1}</span>
                </div>
                <h4 className="text-xl md:text-2xl font-heading text-accent uppercase mb-3">
                  {step.title}
                </h4>
                <p className="text-sm md:text-base text-stone-light font-body leading-relaxed mb-3">
                  {step.description}
                </p>
                <p className="text-xs text-accent/80 font-body italic border-t border-bone-white/10 pt-3 mt-3">
                  <i className="fas fa-lightbulb mr-1"></i> {step.tip}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="tel:850-615-6105"
            data-testid="pawn-call-btn"
            className="cave-btn inline-flex items-center gap-3 px-8 py-4 bg-accent text-dirt-brown font-heading text-base md:text-lg uppercase tracking-wider rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all duration-300"
          >
            <Phone size={22} />
            Ready to Start? Call 850-615-6105
          </a>
        </div>
      </div>

      {/* Boulder wave bottom */}
      <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute -bottom-1 left-0 w-full">
        <path d="M0 80L60 68C120 56 240 32 360 26C480 20 600 32 720 38C840 44 960 44 1080 38C1200 32 1320 20 1380 14L1440 8V80H0Z" fill="#F4EBD9"/>
      </svg>
    </section>
  );
}
